import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import api from "../../services/api";
import PasswordInput from "../../components/common/PasswordInput";
import { fetchCurrentUser, logout, selectCurrentUser, selectIsAuthenticated } from "../../features/auth/authSlice";

// Shown right after the first login: the emailed password is system-generated, so the user must pick their own.
export default function ForceChangePasswordPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const user = useSelector(selectCurrentUser);
  const [form, setForm] = useState({ old_password: "", new_password: "", confirm_password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (user && !user.must_change_password) return <Navigate to="/dashboard" replace />;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.new_password.length < 8) return setError("New password must be at least 8 characters.");
    if (form.new_password !== form.confirm_password) return setError("The two new passwords do not match.");
    setLoading(true);
    try {
      await api.post("/auth/change-password/", { old_password: form.old_password, new_password: form.new_password });
      await dispatch(fetchCurrentUser());
      toast.success("Password changed. Welcome!");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      const d = err.response?.data || {};
      setError(d.new_password || d.error || d.detail || "Could not change the password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={S.page}>
      <form onSubmit={submit} style={S.card}>
        <h2 style={{ margin: "0 0 6px", color: "#0F172A" }}>Choose your own password</h2>
        <p style={{ margin: "0 0 20px", color: "#64748B", fontSize: 14, lineHeight: 1.5 }}>
          Your account was created with a system-generated password. For your security, please set a new one before you continue.
        </p>
        <label style={S.label}>Current (emailed) password *</label>
        <PasswordInput style={S.input} value={form.old_password} onChange={set("old_password")} autoComplete="current-password" required />
        <label style={S.label}>New password *</label>
        <PasswordInput style={S.input} value={form.new_password} onChange={set("new_password")} autoComplete="new-password" minLength={8} required />
        <label style={S.label}>Confirm new password *</label>
        <PasswordInput style={S.input} value={form.confirm_password} onChange={set("confirm_password")} autoComplete="new-password" required />
        {error && <div style={S.error}>{error}</div>}
        <button type="submit" disabled={loading} style={{ ...S.btn, opacity: loading ? 0.7 : 1 }}>
          {loading ? "Saving..." : "Save password & continue"}
        </button>
        <button type="button" onClick={() => { dispatch(logout()); navigate("/login", { replace: true }); }} style={S.link}>
          Sign out
        </button>
      </form>
    </div>
  );
}

const S = {
  page: { minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#F1F5F9", padding: 16 },
  card: { width: "100%", maxWidth: 420, background: "#fff", borderRadius: 14, padding: 28, boxShadow: "0 10px 40px rgba(15,23,42,0.12)" },
  label: { display: "block", fontSize: 13, fontWeight: 600, color: "#475467", margin: "14px 0 5px" },
  input: { padding: "11px 14px", borderRadius: 8, border: "1.5px solid #E2E8F0", fontSize: 14, outline: "none", fontFamily: "inherit" },
  error: { marginTop: 14, color: "#DC2626", fontSize: 13 },
  btn: { width: "100%", marginTop: 20, padding: 13, borderRadius: 8, border: "none", background: "#2E86AB", color: "#fff", fontWeight: 700, fontSize: 15, cursor: "pointer" },
  link: { display: "block", margin: "12px auto 0", background: "none", border: "none", color: "#64748B", fontSize: 13, cursor: "pointer" },
};
